/**
 * MCP Organization Context
 * Handles multi-tenant context and permissions
 */

import { MCPExecutionContext, MCPOrgConfig } from '../types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

export class MCPOrgContext {
  private contextCache: Map<string, { context: MCPExecutionContext; cachedAt: Date }> = new Map();
  private cacheDuration = 5 * 60 * 1000; // 5 minutes

  /**
   * Create execution context for a request
   */
  async createContext(orgId: string, userId: string): Promise<MCPExecutionContext> {
    // Check cache
    const cacheKey = `${orgId}:${userId}`;
    const cached = this.contextCache.get(cacheKey);
    
    if (cached && (Date.now() - cached.cachedAt.getTime()) < this.cacheDuration) {
      return cached.context;
    }

    // Fetch org configuration
    const orgConfig = await this.getOrgConfig(orgId);
    
    // Check if MCP is enabled
    if (!orgConfig.mcp.enabled) {
      throw new Error(`MCP is not enabled for organization: ${orgId}`);
    }

    // Fetch user info
    const userRole = await this.getUserRole(userId, orgId);

    const context: MCPExecutionContext = {
      orgId,
      userId,
      userRole,
      orgConfig,
      timestamp: new Date(),
      requestId: this.generateRequestId()
    };

    // Cache context
    this.contextCache.set(cacheKey, {
      context,
      cachedAt: new Date()
    });

    return context;
  }

  /**
   * Get organization configuration
   */
  private async getOrgConfig(orgId: string): Promise<MCPOrgConfig> {
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      
      if (!orgDoc.exists()) {
        // Return default config if org doesn't exist yet
        return this.getDefaultOrgConfig(orgId);
      }

      const data = orgDoc.data();
      
      // Merge with defaults
      return {
        orgId,
        orgName: data.name || 'Unknown Organization',
        mcp: {
          enabled: data.mcp?.enabled ?? true, // ✅ MCP enabled by default for all orgs
          serverUrl: data.mcp?.serverUrl,
          allowedTools: data.mcp?.allowedTools || ['*'], // '*' means all tools
          allowedResources: data.mcp?.allowedResources || ['*']
        },
        aiProviders: {
          openai: data.aiProviders?.openai,
          claude: data.aiProviders?.claude,
          custom: data.aiProviders?.custom
        },
        features: {
          agentMode: data.features?.agentMode ?? true,
          voiceAgent: data.features?.voiceAgent ?? true,
          mcpIntegration: data.features?.mcpIntegration ?? true,
          externalTools: data.features?.externalTools ?? true
        },
        limits: {
          requestsPerMinute: data.limits?.requestsPerMinute || 60,
          requestsPerDay: data.limits?.requestsPerDay || 10000,
          maxToolExecutionTime: data.limits?.maxToolExecutionTime || 30
        }
      };
    } catch (error) {
      console.error('Error fetching org config:', error);
      return this.getDefaultOrgConfig(orgId);
    }
  }

  /**
   * Get default organization configuration
   */
  private getDefaultOrgConfig(orgId: string): MCPOrgConfig {
    return {
      orgId,
      orgName: 'Default Organization',
      mcp: {
        enabled: true,
        allowedTools: ['*'],
        allowedResources: ['*']
      },
      aiProviders: {},
      features: {
        agentMode: true,
        voiceAgent: true,
        mcpIntegration: true,
        externalTools: true
      },
      limits: {
        requestsPerMinute: 60,
        requestsPerDay: 10000,
        maxToolExecutionTime: 30
      }
    };
  }

  /**
   * Get user role in organization
   */
  private async getUserRole(
    userId: string,
    orgId: string
  ): Promise<'admin' | 'user' | 'viewer'> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        return 'viewer';
      }

      const data = userDoc.data();
      
      // Check if user belongs to org
      if (data.orgId !== orgId) {
        throw new Error('User does not belong to this organization');
      }

      return data.role || 'user';
    } catch (error) {
      console.error('Error fetching user role:', error);
      return 'viewer';
    }
  }

  /**
   * Validate access to a specific tool
   */
  async validateToolAccess(
    context: MCPExecutionContext,
    toolName: string
  ): Promise<boolean> {
    const allowedTools = context.orgConfig.mcp.allowedTools;
    
    // Check if all tools are allowed
    if (allowedTools.includes('*')) {
      return true;
    }

    // Check if specific tool is allowed
    return allowedTools.includes(toolName);
  }

  /**
   * Validate access to a specific resource
   */
  async validateResourceAccess(
    context: MCPExecutionContext,
    resourceUri: string
  ): Promise<boolean> {
    const allowedResources = context.orgConfig.mcp.allowedResources;
    
    // Check if all resources are allowed
    if (allowedResources.includes('*')) {
      return true;
    }

    // Check if specific resource pattern is allowed
    return allowedResources.some(pattern => {
      if (pattern.endsWith('*')) {
        return resourceUri.startsWith(pattern.slice(0, -1));
      }
      return resourceUri === pattern;
    });
  }

  /**
   * Check if user has admin privileges
   */
  isAdmin(context: MCPExecutionContext): boolean {
    return context.userRole === 'admin';
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clear context cache
   */
  clearCache(): void {
    this.contextCache.clear();
  }

  /**
   * Clear cache for specific org/user
   */
  clearCacheFor(orgId: string, userId?: string): void {
    if (userId) {
      this.contextCache.delete(`${orgId}:${userId}`);
    } else {
      // Clear all entries for org
      for (const key of this.contextCache.keys()) {
        if (key.startsWith(`${orgId}:`)) {
          this.contextCache.delete(key);
        }
      }
    }
  }
}

